import { Modal } from '@/components';
import { useUploadSitePhoto } from '@/remote/site-management/site-photos';
import { useState } from 'react';

interface SitePhotoUploadModalProps {
  isOpen: boolean;
  siteId: string;
  onClose: () => void;
  onUploadSuccess?: () => void;
}

export default function SitePhotoUploadModal({
  isOpen,
  siteId,
  onClose,
  onUploadSuccess,
}: SitePhotoUploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [photoTitle, setPhotoTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const uploadMutation = useUploadSitePhoto();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // 파일 크기 체크 (10MB 제한)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('파일 크기는 10MB 이하여야 합니다.');
        return;
      }

      // 이미지 파일 타입 체크
      if (!selectedFile.type.startsWith('image/')) {
        setError('이미지 파일만 업로드할 수 있습니다.');
        return;
      }

      setFile(selectedFile);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file || !photoTitle.trim()) {
      setError('사진 파일과 제목을 모두 입력해주세요.');
      return;
    }

    try {
      await uploadMutation.mutateAsync({
        siteId,
        file,
        title: photoTitle.trim(),
      });

      // 업로드 성공 시 폼 초기화
      handleClose();
      onUploadSuccess?.();
    } catch (error) {
      console.error('사진 업로드 실패:', error);
      setError('사진 업로드에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const handleClose = () => {
    setFile(null);
    setPhotoTitle('');
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="현장 사진 업로드"
      size="md"
    >
      <div className="p-6 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            사진 제목 *
          </label>
          <input
            type="text"
            value={photoTitle}
            onChange={(e) => setPhotoTitle(e.target.value)}
            placeholder="사진 제목을 입력하세요"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={uploadMutation.isPending}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            사진 파일 *
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={uploadMutation.isPending}
          />
          <p className="text-xs text-gray-500 mt-1">
            10MB 이하의 이미지 파일만 가능합니다. <br />
            JPG, PNG, GIF, WebP의 확장자만 가능합니다.
          </p>

          {file && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)}MB
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={uploadMutation.isPending}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            type="button"
            onClick={handleUpload}
            disabled={!file || !photoTitle.trim() || uploadMutation.isPending}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2 rounded-lg whitespace-nowrap flex items-center justify-center space-x-2"
          >
            {uploadMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>업로드 중...</span>
              </>
            ) : (
              <span>업로드</span>
            )}
          </button>
          <button
            type="button"
            onClick={handleClose}
            disabled={uploadMutation.isPending}
            className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed py-2 rounded-lg whitespace-nowrap"
          >
            취소
          </button>
        </div>
      </div>
    </Modal>
  );
}
